import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KEY = "kc_booking_draft";
export const STEPS = ["Your Home", "Choose Cleaning", "Date & Time", "Review & Confirm"];
export const PREFILL_KEY = "kc_booking_prefill";

export const EMPTY = {
  step: 0,
  address: { street: "", unit: "", city: "", province: "ON", postal_code: "", notes: "" },
  quote: { service_code: "standard", property_type: "house", bedrooms: 3, full_bathrooms: 2, powder_rooms: 0, kitchens: 1, additional_kitchens: 0,
    has_basement: false, has_stairs: false, is_airbnb: false, hours: null, customer_provides_supplies: false, frequency: "one_time", addon_codes: [],
    promo_code: "", reward_id: null, referral_code: "", special_type: "kitchen", washrooms: 2 },
  guest: { first_name: "", last_name: "", email: "", phone: "" }, as_guest: false,
  scheduled_date: "", time_slot: "", time_display: "", notes: "", customer_phone: "",
};

const BookingContext = createContext(null);

export function BookingProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
      const pre = JSON.parse(localStorage.getItem(PREFILL_KEY) || "null");
      localStorage.removeItem(PREFILL_KEY);
      const base = { ...EMPTY, ...saved, step: Math.min(saved.step || 0, STEPS.length - 1) };
      if (pre) return { ...base, step: 0, quote: { ...EMPTY.quote, ...(pre.quote || {}) }, address: { ...EMPTY.address, ...(pre.address || {}) }, scheduled_date: pre.scheduled_date || "", time_slot: pre.time_slot || "", time_display: "" };
      return base;
    } catch { return EMPTY; }
  });
  const [priceQuote, setPriceQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [area, setArea] = useState(null);
  const timer = useRef();

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(state)); }, [state]);
  useEffect(() => {
    if (!user) return;
    api.get("/bookings/draft/current").then((r) => {
      if (r.data && !localStorage.getItem(KEY)) setState((s) => ({ ...s, ...r.data }));
    }).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => api.put("/bookings/draft", state).catch(() => {}), 1200);
    return () => clearTimeout(t);
  }, [state, user]);

  const patch = useCallback((p) => setState((s) => ({ ...s, ...p })), []);
  const patchQuote = useCallback((p) => setState((s) => ({ ...s, quote: { ...s.quote, ...p } })), []);
  const patchAddress = useCallback((p) => setState((s) => ({ ...s, address: { ...s.address, ...p } })), []);
  const reset = useCallback(() => { localStorage.removeItem(KEY); setState(EMPTY); setPriceQuote(null); }, []);

  const payload = useMemo(() => ({ ...state.quote, hours: state.quote.hours || null, promo_code: state.quote.promo_code || null,
    referral_code: state.quote.referral_code || null, postal_code: state.address.postal_code || null, city: state.address.city || null, scheduled_date: state.scheduled_date || null }), [state.quote, state.address.postal_code, state.address.city, state.scheduled_date]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post("/public/quote", payload).then((r) => { setPriceQuote(r.data); setQuoteError(""); })
        .catch(() => setQuoteError("We couldn't calculate your price right now. Your information is saved. Please try again."));
    }, 350);
    return () => clearTimeout(timer.current);
  }, [payload, state.step, user]);

  const checkArea = useCallback(async (postal_code) => {
    const r = await api.get("/public/service-areas/check", { params: { postal_code } });
    setArea(r.data); return r.data;
  }, []);

  return <BookingContext.Provider value={{ state, patch, patchQuote, patchAddress, reset, priceQuote, quoteError, area, checkArea, setArea }}>{children}</BookingContext.Provider>;
}

export const useBooking = () => useContext(BookingContext);
